import React from 'react';

interface LearningPathProps {
  completed: number;
  total: number;
  tone?: 'light' | 'dark';
  className?: string;
}

/**
 * A single, deliberate progress treatment for the LMS: a route map with
 * milestone markers and one current-position pin. It replaces the repeated
 * percentage bars that used to appear in the player header, sidebar, dashboard
 * hero and programme cards.
 */
const LearningPath: React.FC<LearningPathProps> = ({ completed, total, tone = 'light', className = '' }) => {
  const safeTotal = Math.max(total, 0);
  const safeCompleted = Math.max(0, Math.min(completed, safeTotal));
  const pct = safeTotal === 0 ? 0 : Math.round((safeCompleted / safeTotal) * 100);
  const markerCount = 9;
  const dark = tone === 'dark';
  const pinPosition = Math.min(98, Math.max(2, pct));

  return (
    <div
      className={className}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      aria-label={`${safeCompleted} of ${safeTotal} lessons complete`}
    >
      <div className="relative h-11">
        <div
          aria-hidden="true"
          className={`absolute left-0 right-0 top-[28px] h-px ${dark ? 'bg-white/20' : 'bg-slate-200'}`}
        />
        {Array.from({ length: markerCount }).map((_, index) => {
          const position = (index / (markerCount - 1)) * 100;
          const reached = pct >= position;
          return (
            <span
              key={index}
              aria-hidden="true"
              className={`absolute top-[24px] h-[9px] w-[9px] -translate-x-1/2 rounded-full border transition-colors ${
                reached
                  ? dark
                    ? 'border-sky-200 bg-sky-200'
                    : 'border-blue-600 bg-blue-600'
                  : dark
                    ? 'border-white/30 bg-blue-950'
                    : 'border-slate-300 bg-white'
              }`}
              style={{ left: `${position}%` }}
            />
          );
        })}

        <div
          className="absolute top-0 -translate-x-1/2 transition-[left] duration-700 ease-out"
          style={{ left: `${pinPosition}%` }}
        >
          <span
            className={`tnum inline-flex min-w-[38px] justify-center rounded-full px-2 py-1 font-mono text-[9px] font-semibold shadow-sm ${
              dark ? 'bg-white text-blue-800' : 'bg-blue-600 text-white'
            }`}
          >
            {pct}%
          </span>
          <span
            aria-hidden="true"
            className={`mx-auto mt-1.5 block h-2.5 w-2.5 rounded-full ring-4 ${
              dark ? 'bg-white ring-white/15' : 'bg-blue-600 ring-blue-100'
            }`}
          />
        </div>
      </div>

      <div
        className={`mt-1.5 flex items-center justify-between gap-3 font-mono text-[8.5px] uppercase tracking-[0.1em] ${
          dark ? 'text-blue-100/65' : 'text-slate-400'
        }`}
      >
        <span>Start</span>
        <span className={dark ? 'text-blue-50/90' : 'text-slate-500'}>
          {safeCompleted} of {safeTotal} lessons
        </span>
        <span>Certificate</span>
      </div>
    </div>
  );
};

export default LearningPath;
